import React, { useMemo, useState } from 'react';
import { ImageList, ImageListItem } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import CommonLoader from '../../../components/common-loader/CommonLoader';
import ContentCard from './card/ContentCard';
import CommonPagination from '../../../components/common-pagination/CommonPagination';
import ContentModal from '../content-modal/ContentModal';
import { changeActiv8Tab, changeTableSettings } from '../../../redux/activ8/activ8Reducer';
import useGridElementsCount from '../../hooks/useGridElementsCount';
import UploadDisclaimer from './upload-disclaimer/UploadDisclaimer';
import NoData from './no-data/NoData';
import { useProject } from '../../../context/ProjectContext';
import { ACTIV8_TABS, REACTIONS } from '../../../constants';
import downloadFile from '../../../utils/downloadFile';
import useOpenStudioController from '../../hooks/useOpenStudioController';
import ConfirmationModal from '../../../components/confirmation-modal/ConfirmationModal';
import { clearBeforeInit, setSelectedMedia, setSelectedMediaActiveId } from '../../../redux/story/storyReducer';
import { normalizeFromFind } from '../../story/media/storyMediaItem';

import scss from './ContentList.module.scss';

function ContentList() {
  const { t } = useTranslation('activ8');
  const dispatch = useDispatch();
  const { id: projectId } = useParams();
  const { actions } = useProject();

  const [previewData, setPreviewData] = useState({});
  const content = useSelector((state) => state.activ8.content);
  const isLoading = useSelector((state) => state.activ8.isLoading);
  const { page, size, totalElements, searchQuery, filters } = useSelector((state) => state.activ8.tableSettings);

  const isSearched = useMemo(
    () => searchQuery || Object.values(filters).some((filter) => filter.length),
    [searchQuery, filters],
  );

  const colsCount = useGridElementsCount();
  const { confirmData, onCloseConfirmModal, onConfirmOpenStudio, onTryToOpenStudio } = useOpenStudioController();

  const handleChangePage = (newPage) => {
    dispatch(changeTableSettings({ page: newPage }));
  };

  const handlePreview = ({ bucketId, initialIndex }) => {
    setPreviewData({ bucketId, initialIndex });
  };

  const closePreview = () => {
    setPreviewData({});
  };

  const handleLike = (slide) => {
    actions('likeContent', { id: slide.id, reaction: slide.reaction === REACTIONS.LIKE ? '' : REACTIONS.LIKE });
  };

  const handleDelete = (slide) => {
    actions('deleteContent', { contentId: slide.id, projectId: projectId });
  };

  const handleDownload = ({ fileName, contentDownloadLink }) => {
    if (contentDownloadLink) {
      downloadFile(contentDownloadLink, fileName);
    }
  };

  const handleDownloadSource = async ({ id }) => {
    const {
      payload: { file, fileName },
    } = await actions('downloadSource', { id });

    if (file) {
      downloadFile(file, fileName);
    }
  };

  const handleOpenStory = (slide) => {
    dispatch(clearBeforeInit());
    dispatch(setSelectedMedia([normalizeFromFind(slide)]));
    dispatch(setSelectedMediaActiveId(slide.id));
    dispatch(changeActiv8Tab(ACTIV8_TABS.STORY));
  };

  return (
    <div className={scss.wrapper}>
      {!content.length && !isSearched && !isLoading ? <UploadDisclaimer /> : null}
      {!content.length && isSearched ? <NoData /> : null}

      {content.length ? (
        <>
          <ImageList variant='masonry' cols={colsCount} gap={8}>
            {content.map((item) => (
              <ImageListItem key={item.bucketId}>
                <ContentCard
                  media={item.media}
                  bucketId={item.bucketId}
                  onPreview={handlePreview}
                  onLike={handleLike}
                  onDownload={handleDownload}
                  onDownloadSource={handleDownloadSource}
                  onStudio={onTryToOpenStudio}
                  onDelete={handleDelete}
                  onStory={handleOpenStory}
                />
              </ImageListItem>
            ))}
          </ImageList>

          <div className={scss.paginationWrapper}>
            <CommonPagination
              totalItemsCount={totalElements}
              itemsPerPage={size}
              activePage={page}
              onChangePage={handleChangePage}
            />
          </div>
        </>
      ) : null}

      <CommonLoader isLoading={isLoading} />

      <ContentModal
        isOpen={!!previewData?.bucketId}
        onClose={closePreview}
        initialIndex={previewData?.initialIndex}
        bucketId={previewData?.bucketId}
        onLike={handleLike}
        onDownload={handleDownload}
        onDownloadSource={handleDownloadSource}
        onStudio={onTryToOpenStudio}
        onDelete={handleDelete}
        onStory={handleOpenStory}
      />

      <ConfirmationModal
        isOpen={!!confirmData}
        onClose={onCloseConfirmModal}
        onCancel={onCloseConfirmModal}
        onConfirm={onConfirmOpenStudio}
        label={t('doYouWantToOpenStudio')}
        subLabel={t('allMediaThatWasCreatedInPreviousStudioSessionWillBeLost')}
      />
    </div>
  );
}

export default ContentList;
